import { Dispatch, SetStateAction, useEffect } from "react";
import { DownloadIcon, EditIcon } from "components/icons";

import { useState, useRef, useMemo, useCallback } from "react";

import { ImageGallery } from "./ImageGalleryNew";
import LoadingSpinner from "components/LoadingSpinner";

import type { StoredFile } from "hooks/useProcessFiles";

import { useTextTruncation } from "hooks/useTextTruncation";

// 生成图像
export type IGeneratedImage =
  | {
      prompt: string;
      images: StoredFile[];
      editFileId?: string;
    }
  | undefined;

type ImagePreviewProps = {
  generationImages: IGeneratedImage;
  isLoading?: boolean;
  setSelectedFiles: Dispatch<SetStateAction<Record<string, StoredFile>>>;
  onRemix?: (storedFile: StoredFile) => void;
  onDownloadImage?: (imageUrl: string) => void;
  onVisibleChange?: () => void;
};

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  generationImages,
  isLoading,
  // setSelectedFiles,
  onRemix,
  onDownloadImage,
  onVisibleChange,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const { maxChars, isTruncated } = useTextTruncation({
    text: generationImages?.prompt || "",
    maxLines: 2,
    reservedWidth: 80,
    containerRef,
    textRef,
  });

  // 计算显示文本
  const displayText = useMemo(() => {
    if (isExpanded || !isTruncated) return generationImages?.prompt || "";
    return `${generationImages?.prompt.slice(0, maxChars)}...`;
  }, [isExpanded, isTruncated, generationImages?.prompt, maxChars]);

  // 切换展开/收起状态
  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Download
  const handleDownload = () => {
    if (!generationImages) {
      return;
    }

    const totalImageLength = generationImages?.images?.length;

    const current =
      activeIndex >= totalImageLength ? totalImageLength - 1 : activeIndex;

    const imageUrl = generationImages?.images[current]?.preview;
    if (imageUrl) {
      onDownloadImage?.(imageUrl);
    }
  };

  const handleRemix = () => {
    // alert("This feature has not been implemented yet");

    if (!generationImages) {
      return;
    }

    const totalImageLength = generationImages?.images?.length;

    const current =
      activeIndex >= totalImageLength ? totalImageLength - 1 : activeIndex;

    const storedFile = generationImages?.images[current];
    if (onRemix && storedFile) {
      onRemix?.(storedFile);
    }
  };

  useEffect(() => {
    if (activeIndex !== 0) {
      setActiveIndex(0);
    }
    // eslint-disable-next-line
  }, [generationImages]);

  return (
    <div
      className="border border-solid border-gray-300 rounded-lg bg-white p-2 md:p-4 md:mt-3"
      ref={containerRef}
    >
      {generationImages &&
      generationImages.images &&
      generationImages.images?.length > 0 &&
      !isLoading ? (
        <>
          <div className="mb-2">Your Generated Asset 🎉</div>
          <div className="text-[14px] font-medium text-[#2563EB] p-2 rounded-[4px] bg-[#DBEAFE] mb-[18px]">
            <span ref={textRef}>
              {`“${displayText}”` || ""}
              {isTruncated && !isExpanded && (
                <button
                  className="ml-2 text-[#818CF8] underline"
                  onClick={toggleExpand}
                >
                  More
                </button>
              )}
              {isExpanded && (
                <button
                  className="ml-2 text-[#818CF8] underline"
                  onClick={toggleExpand}
                >
                  Less
                </button>
              )}
            </span>
          </div>
          <ImageGallery
            editFileId={generationImages?.editFileId}
            images={generationImages?.images || []}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            onVisibleChange={onVisibleChange}
          />
        </>
      ) : (
        <>
          <div className="mb-[18px]">Your Asset is being generated... ⏳</div>
          <div className="bg-gray-200 w-full h-[300px] md:h-[600px] rounded-[8px] flex flex-row items-center justify-center text-gray-500 text-[14px] text-center">
            {isLoading ? (
              <LoadingSpinner color="#4338CA" size="middle" />
            ) : (
              <span>The generated image will be displayed here.</span>
            )}
          </div>
        </>
      )}

      {!isLoading &&
        generationImages &&
        generationImages.images?.length > 0 && (
          <div className="flex flex-row flex-wrap items-stretch justify-start gap-[8px] mt-[18px]">
            {/* <button
            disabled={!currentImage || isLoading}
            className="border flex-1 bg-white rounded-[6px] flex flex-row items-center justify-center gap-[8px] border-solid border-[#4338CA] py-[6px] px-[12px] text-[#3730A3] text-[12px] font-semibold disabled:border-gray-300 disabled:text-gray-400"
            onClick={handleShare}
          >
            <ShareIcon /> Share
          </button> */}
            <button
              disabled={isLoading}
              className="border flex-1 bg-white rounded-[6px] flex flex-row items-center justify-center gap-[8px] border-solid border-[#4338CA] py-[6px] px-[12px] text-[#3730A3] text-[12px] font-semibold disabled:border-gray-300 disabled:text-gray-400"
              onClick={handleDownload}
            >
              <DownloadIcon />
              Download
            </button>
            <button
              disabled={isLoading}
              onClick={handleRemix}
              className="border flex-1 bg-[#4338CA] rounded-[6px] flex flex-row items-center justify-center gap-[8px] border-solid border-[#4338CA] py-[6px] px-[12px] text-white text-[12px] font-semibold disabled:border-gray-300 disabled:bg-gray-400 disabled:text-white"
            >
              <EditIcon /> Edit
            </button>
          </div>
        )}
    </div>
  );
};
